<template>
    <settings-wrapper title="Organization settings" section="Integrations">
        <v-row v-if="visibleIntegrations.length">
            <v-col v-for="integration in visibleIntegrations" :key="integration.key" cols="12" sm="6" md="4">
                <v-card flat elevation="3" class="h-100 d-flex flex-column">
                    <v-list three-line color="transparent">
                        <v-list-item>
                            <v-list-item-avatar>
                                <img src="@/assets/switch-favicon.svg" alt="Switch logo" class="switch-logo" />
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ integration.title }}</v-list-item-title>
                                <v-list-item-subtitle>{{ integration.description }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-icon>
                                <img src="@/assets/swiss-flag.svg" alt="Swiss" class="swiss-flag-icon" />
                            </v-list-item-icon>
                        </v-list-item>
                    </v-list>
                    <v-card-actions>
                        <v-btn color="primary" text @click="openConfigureDialog(integration)">Configure</v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
        <dashboard-empty v-else class="mt-16">No integrations available.</dashboard-empty>

        <v-dialog v-model="showConfigureDialog" max-width="850" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <div class="d-flex align-center secondary--text">
                        <span>Configure {{ selectedIntegrationTitle }}</span>
                    </div>

                    <v-spacer></v-spacer>
                    <v-btn @click="showConfigureDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-form ref="configureForm" class="mt-4">
                        <div class="d-flex align-start">
                            <v-text-field
                                v-model="applicationCredentialId"
                                label="Application credential ID"
                                outlined
                                dense
                                :rules="[rules.required]"
                                :disabled="validatingApplicationCredential || savingApplicationCredential"
                                required
                                class="flex-grow-1 mr-2"></v-text-field>
                            <v-text-field
                                v-model="applicationCredentialSecret"
                                outlined
                                :append-icon="showApplicationCredentialSecret ? 'visibility' : 'visibility_off'"
                                :type="showApplicationCredentialSecret ? 'text' : 'password'"
                                @click:append="showApplicationCredentialSecret = !showApplicationCredentialSecret"
                                dense
                                :rules="[rules.required]"
                                :disabled="validatingApplicationCredential || savingApplicationCredential"
                                required
                                class="flex-grow-1">
                                <template v-slot:label>Application credential Secret</template>
                            </v-text-field>
                            <v-btn v-if="applicationCredentialSaveSuccess" text class="ml-2 mt-1" color="success">
                                <v-icon>check</v-icon>
                            </v-btn>
                            <v-btn
                                v-else
                                text
                                class="ml-2 mt-1"
                                :loading="savingApplicationCredential"
                                :disabled="
                                    validatingApplicationCredential || savingApplicationCredential || !applicationCredentialId || !applicationCredentialSecret
                                "
                                color="primary"
                                @click="saveApplicationCredential">
                                <span>Save</span>
                            </v-btn>
                            <v-btn
                                text
                                class="ml-2 mt-1"
                                :loading="validatingApplicationCredential"
                                :disabled="
                                    validatingApplicationCredential || savingApplicationCredential || !applicationCredentialId || !applicationCredentialSecret
                                "
                                color="primary"
                                @click="validateApplicationCredential">
                                <span>Validate</span>
                            </v-btn>
                        </div>
                        <div v-if="applicationCredentialValidationSuccess" class="success--text caption mb-6 d-flex align-center">
                            <v-icon small color="success" class="mr-1">check_circle</v-icon>
                            <span>Successfully connected to Switch Cloud.</span>
                        </div>
                        <div class="d-flex align-start">
                            <v-text-field
                                v-model="project"
                                label="Project"
                                outlined
                                dense
                                :rules="[rules.required]"
                                :disabled="renamingProject"
                                required
                                class="flex-grow-1"></v-text-field>
                            <v-btn v-if="projectRenameSuccess" text class="ml-2 mt-1" color="success">
                                <v-icon>check</v-icon>
                            </v-btn>
                            <v-btn
                                v-else
                                text
                                class="ml-2 mt-1"
                                :loading="renamingProject"
                                :disabled="renamingProject || !project"
                                color="primary"
                                @click="renameProject">
                                <span>Rename</span>
                            </v-btn>
                        </div>
                    </v-form>

                    <div class="mb-3">
                        <b>Project quota</b>
                    </div>
                    <v-card outlined flat>
                        <v-card-text>
                            <v-row v-for="section in projectUsageSchema" :key="section.section">
                                <v-col cols="12">
                                    {{ section.section }}
                                </v-col>
                                <v-col v-for="metric in section.metrics" :key="metric.key" cols="12" sm="6" md="4">
                                    <v-card flat class="grey lighten-3 h-100">
                                        <v-card-text class="py-3">
                                            <div class="d-flex align-center">
                                                <span class="caption font-weight-medium">{{ metric.label }}</span>
                                                <v-spacer></v-spacer>
                                                <span class="caption">{{ `${metricUsagePercent(metric)}%` }}</span>
                                            </div>
                                            <v-progress-linear
                                                rounded
                                                height="6"
                                                :value="metricUsagePercent(metric)"
                                                :color="metricUsagePercent(metric) <= 90 ? 'primary' : 'error'"
                                                class="mb-2"></v-progress-linear>
                                            <div class="caption font-weight-medium d-flex align-center">
                                                {{ `${metricDisplayValue(metric, 'used')} of ${metricDisplayValue(metric, 'total')}` }}
                                                used
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-card-text>
            </v-card>
        </v-dialog>
    </settings-wrapper>
</template>

<script>
import SettingsWrapper from '@/components/SettingsWrapper.vue'
import { humanFileSize } from '@/utils'
const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const SWITCH_CLOUD_HARDCODED_CONFIG = {
    applicationCredentialId: 'be653c2d8efb454eafcf1c0044a9f21b',
    applicationCredentialSecret: 'Gl6tjDAn0onqjcjeR2RJ9mxn9XQy-S2bCjVR9pWKR_5jD9tMnzsCMf1ebf_o9d1KS4SZnN2zKaUMnDa7RTVprA',
    project: 'nuvolos.cloud integration'
}
const PROJECT_USAGE_SCHEMA = [
    {
        section: 'Compute',
        metrics: [
            { key: 'instances', label: 'Instances', used: 2, total: 20, unit: '' },
            { key: 'vcpu', label: 'vCPU', used: 8, total: 20, unit: '' },
            { key: 'memory', label: 'Memory', used: 16, total: 80, unit: 'GB' }
        ]
    },
    {
        section: 'Volume',
        metrics: [
            { key: 'volumes', label: 'Volumes', used: 3, total: 40, unit: '' },
            { key: 'snapshots', label: 'Volume snapshots', used: 0, total: 40, unit: '' },
            {
                key: 'storage',
                label: 'Volume storage',
                used: 150,
                total: 2,
                usedUnit: 'GB',
                totalUnit: 'TB',
                usedBytes: 150 * 1000 ** 3,
                totalBytes: 2 * 1000 ** 4
            }
        ]
    }
]

export default {
    name: 'OrganizationConfigIntegrations',
    components: { SettingsWrapper, DashboardEmpty },
    computed: {
        visibleIntegrations() {
            if (!this.isSwitchCloudEnabled()) return []
            return this.integrations
        }
    },
    data() {
        return {
            showConfigureDialog: false,
            selectedIntegrationTitle: '',
            applicationCredentialId: SWITCH_CLOUD_HARDCODED_CONFIG.applicationCredentialId,
            applicationCredentialSecret: SWITCH_CLOUD_HARDCODED_CONFIG.applicationCredentialSecret,
            showApplicationCredentialSecret: false,
            savingApplicationCredential: false,
            applicationCredentialSaveSuccess: false,
            applicationCredentialSaveSuccessTimeout: null,
            validatingApplicationCredential: false,
            applicationCredentialValidationSuccess: false,
            project: SWITCH_CLOUD_HARDCODED_CONFIG.project,
            renamingProject: false,
            projectRenameSuccess: false,
            projectRenameSuccessTimeout: null,
            projectUsageSchema: PROJECT_USAGE_SCHEMA,
            rules: {
                required: v => !!v || 'Field is required'
            },
            integrations: [
                {
                    key: 'switch',
                    title: 'Switch Cloud',
                    description: 'Swiss cloud services with secure, reliable data hosting.'
                }
            ]
        }
    },
    methods: {
        isSwitchCloudEnabled() {
            return false
        },
        openConfigureDialog(integration) {
            this.selectedIntegrationTitle = integration.title
            this.applicationCredentialId = SWITCH_CLOUD_HARDCODED_CONFIG.applicationCredentialId
            this.applicationCredentialSecret = SWITCH_CLOUD_HARDCODED_CONFIG.applicationCredentialSecret
            this.showApplicationCredentialSecret = false
            this.savingApplicationCredential = false
            this.applicationCredentialSaveSuccess = false
            if (this.applicationCredentialSaveSuccessTimeout) {
                clearTimeout(this.applicationCredentialSaveSuccessTimeout)
                this.applicationCredentialSaveSuccessTimeout = null
            }
            this.validatingApplicationCredential = false
            this.applicationCredentialValidationSuccess = false
            this.project = SWITCH_CLOUD_HARDCODED_CONFIG.project
            this.renamingProject = false
            this.projectRenameSuccess = false
            if (this.projectRenameSuccessTimeout) {
                clearTimeout(this.projectRenameSuccessTimeout)
                this.projectRenameSuccessTimeout = null
            }
            this.showConfigureDialog = true
            this.$nextTick(() => {
                if (this.$refs.configureForm) {
                    this.$refs.configureForm.resetValidation()
                }
            })
        },
        fakeRenameProjectApiCall() {
            return new Promise(resolve => setTimeout(resolve, 1200))
        },
        fakeSaveApplicationCredentialApiCall() {
            return new Promise(resolve => setTimeout(resolve, 1000))
        },
        fakeValidateApplicationCredentialApiCall() {
            return new Promise(resolve => setTimeout(resolve, 2000))
        },
        async saveApplicationCredential() {
            if (!this.applicationCredentialId || !this.applicationCredentialSecret || this.savingApplicationCredential || this.validatingApplicationCredential)
                return

            this.applicationCredentialSaveSuccess = false
            this.savingApplicationCredential = true
            await this.fakeSaveApplicationCredentialApiCall()
            this.savingApplicationCredential = false
            this.showApplicationCredentialSecret = false
            this.applicationCredentialSaveSuccess = true

            if (this.applicationCredentialSaveSuccessTimeout) {
                clearTimeout(this.applicationCredentialSaveSuccessTimeout)
            }

            this.applicationCredentialSaveSuccessTimeout = setTimeout(() => {
                this.applicationCredentialSaveSuccess = false
                this.applicationCredentialSaveSuccessTimeout = null
            }, 2000)
        },
        async validateApplicationCredential() {
            if (!this.applicationCredentialId || !this.applicationCredentialSecret || this.validatingApplicationCredential || this.savingApplicationCredential)
                return

            this.applicationCredentialValidationSuccess = false
            this.validatingApplicationCredential = true

            try {
                await this.fakeValidateApplicationCredentialApiCall()
                this.showApplicationCredentialSecret = false
                this.applicationCredentialValidationSuccess = true
            } finally {
                this.validatingApplicationCredential = false
            }
        },
        async renameProject() {
            if (!this.project || this.renamingProject) return

            this.projectRenameSuccess = false
            this.renamingProject = true

            await this.fakeRenameProjectApiCall()

            this.renamingProject = false
            this.projectRenameSuccess = true

            if (this.projectRenameSuccessTimeout) {
                clearTimeout(this.projectRenameSuccessTimeout)
            }

            this.projectRenameSuccessTimeout = setTimeout(() => {
                this.projectRenameSuccess = false
                this.projectRenameSuccessTimeout = null
            }, 2000)
        },
        metricUsagePercent(metric) {
            if (!metric) return 0

            const usedValue = metric.usedBytes ?? Number(metric.used || 0)
            const totalValue = metric.totalBytes ?? Number(metric.total || 0)

            if (!totalValue) return 0
            return Math.floor((usedValue / totalValue) * 100)
        },
        metricDisplayValue(metric, side) {
            const bytes = side === 'used' ? metric.usedBytes : metric.totalBytes
            if (bytes != null) {
                return humanFileSize(bytes, true, 0)
            }

            const value = side === 'used' ? metric.used : metric.total
            const unit = side === 'used' ? metric.usedUnit || metric.unit : metric.totalUnit || metric.unit
            return `${value}${unit || ''}`
        }
    }
}
</script>

<style scoped>
.switch-logo {
    width: 40px;
    height: 40px;
}

.swiss-flag-icon {
    width: 18px;
    height: 18px;
}
</style>
